import React from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import MemberNameCol from './MemberNameCol';
import { IProjectMembersDataTest, IPrjectMemberGridColumns } from '../type';
import ProjectMemberAdminColumns from './project-member-admin-columns';
import NoRecordsOverlay from '../../common/data-no-records';

const statusLabelMap = {
  PENDING: 'Waiting for Approval',
  APPROVED: 'Active',
  REJECTED: 'Rejected'
};

const statusColorMap = {
  PENDING: '#EA9515',
  APPROVED: '#06BF3A',
  REJECTED: '#F3281C'
};

const cellTypoStyle = {
  fontFamily: 'Be Vietnam Pro',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  textAlign: 'left'
};

interface IGridData {
  updateProjectMemberCallback: () => Promise<void>;
  columns: IPrjectMemberGridColumns[];
  isLoading?: boolean;
  data: IProjectMembersDataTest[];
  canEdit?: boolean;
  canDelete?: boolean;
  projectId: string;
  isAdmin: boolean;
}

const ProjectDataGrid = ({
  updateProjectMemberCallback,
  columns,
  isLoading,
  data,
  canEdit = false,
  canDelete = false,
  projectId,
  isAdmin
}: IGridData): JSX.Element => {
  const rows = data?.map((item, index) => ({
    id: item.id,
    rowIndex: index + 1,
    displayName: item.member.displayName,
    memberRoles: item.memberRoles,
    hoursPerWeek: item.hoursPerWeek,
    assignedAt: item.assignedAt
      ? new Date(item.assignedAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
      : '',
    memberStatus: item.memberStatus,
    actionId: item.member.id,
    providerId: item.member.providerId,
    member: item.member,
    roleStartDate: item.roleStartDate,
    roleEndDate: item.roleEndDate
  }));

  const mappedColumns: GridColDef[] = columns.map((col) => {
    if (col.field === 'id') {
      return {
        ...col,
        renderCell: (params) => <Typography>{params.row.rowIndex}</Typography>
      };
    }

    if (col.field === 'displayName') {
      return {
        ...col,
        renderCell: (params) => (
          <MemberNameCol
            id={params.row?.id}
            providerId={params.row?.providerId}
            displayName={params.row?.displayName}
          />
        )
      };
    }

    if (col.field === 'memberRole') {
      return {
        ...col,
        renderCell: (params) => (
          <Typography>
            {params.row.memberRoles?.map(
              (role: { projectRole: { id: string; displayName: string } }) => (
                <React.Fragment key={role.projectRole.id}>
                  {role.projectRole.displayName}
                </React.Fragment>
              )
            )}
          </Typography>
        )
      };
    }

    if (col.field === 'hoursPerWeek' && (canEdit || canDelete || isAdmin)) {
      return col;
    }

    if (col.field === 'memberStatus') {
      return {
        ...col,
        renderCell: (params) => (
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor:
                  statusColorMap[
                    params.row.memberStatus as keyof typeof statusColorMap
                  ] || 'grey',
                marginRight: 1
              }}
            />
            <Typography sx={cellTypoStyle}>
              {
                statusLabelMap[
                  params.row.memberStatus as keyof typeof statusLabelMap
                ]
              }
            </Typography>
          </Box>
        )
      };
    }

    if (col.field === 'actionId' && (canEdit || canDelete || isAdmin)) {
      return {
        ...col,
        renderCell: (params) => (
          <ProjectMemberAdminColumns
            rowItem={params.row}
            updateProjectMemberCallback={updateProjectMemberCallback}
            memberStatus={params.row.memberStatus ?? 'PENDING'}
            projectId={projectId}
            canEdit={true}
            canDelete={
              !params.row.memberRoles.some(
                (role: { projectRole: { role: string } }) =>
                  role.projectRole.role === 'DELIVERY_RESPONSIBLE'
              )
            }
          />
        )
      };
    }

    return col;
  });

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#E5F2FF'
        }
      }}
    >
      <DataGrid
        rows={rows ?? []}
        disableColumnMenu
        disableColumnSelector
        disableVirtualization
        columns={mappedColumns}
        disableSelectionOnClick
        loading={isLoading}
        pagination
        getRowId={(row) => row.id}
        components={{
          NoRowsOverlay: () => (
            <NoRecordsOverlay
              isLoading={isLoading}
              dataLength={rows.length}
              message="No Members Available."
            />
          )
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
            width: '7px',
            height: '7px',
            backgroundColor: 'rgba(217, 217, 217, 0.4)'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#888'
          }
        }}
      />
    </Box>
  );
};

export default ProjectDataGrid;
